const ReloadDefaults = require('relevant-shared/prebid/reloadDefaults');
const Utils = window.relevantDigital.import('Utils');

// We might need to temporary display hidden elements in order to get the correct position
const getClientRect = (elm) => {
	const arr = [];
	let prevX;
	let prevY;
	for (let node = elm; node && getComputedStyle(node, null).display === 'none'; node = node.parentNode) {
		if (node.style) {
			if (prevY === undefined) {
				prevY = window.scrollY;
				prevX = window.scrollX;
			}
			arr.push({ node, display: node.style.display });
			node.style.display = '';
		}
	}
	const res = elm.getBoundingClientRect();
	arr.forEach(({ node, display }) => {
		node.style.display = display;
	});
	if (prevY !== undefined && prevY !== window.scrollY) {
		window.scrollTo(prevX, prevY);
	}
	return res;
};

/** When noSpaceIsEmpty is set we can't immediately be certain that an "ad" is real or not when checking if the
 *  sequence of "empty reloads" should be reset or not. Not until seeing that the ad takes up space >= this many
 *  ms, we'll reset the sequence. */
const EMPTY_CHECK_BUFFER_MS = 10000;

class ReloadState {
	constructor(settings) {
		let { reloadTimes } = settings;
		if (settings.timeBetween <= 0) { // not allowed...
			reloadTimes = 0;
		}
		Utils.assign(this, {
			...ReloadDefaults,
			...settings,
			leftToSchedule: reloadTimes, // number of reloads left
			lastCountTs: null, // Is set to last timestamp of update() if we counted the time at that moment
			countedTime: 0, // How far have we "counted" down until next reload.
			viewableNow: false, // Was placement viewable during last update() call
			emptyReturnRetries: 0,
			filter: (settings.filter || []).filter((f) => f.value || f.liType.length), // Skip "empty" filters
		});
	}

	element() {
		return this.reloader.pbRequester.domInterface.getElementById(this.divId());
	}

	setEmpty(isEmpty = true) {
		const renderStatus = this.renderStatus();
		if (renderStatus && renderStatus !== isEmpty) {
			this.reloader.slotRenderStatusById[this.divId()] = { ...renderStatus, isEmpty };
		}
	}

	renderStatus() {
		const { reloader } = this;
		const status = this.reloader.slotRenderStatusById[this.divId()];
		// Also return null if ad-div is no longer in DOM
		return status && reloader.pbRequester.hasRenderedDivId(this.divId()) ? status : null;
	}

	currEmptyReload(allowNoStatus) {
		const renderStatus = this.renderStatus();
		const remaining = Math.min(this.maxEmptyReturnRetries, 5) - this.emptyReturnRetries;
		const eligable = this.retryOnEmptyReturn
			&& remaining
			&& (renderStatus?.isEmpty || (allowNoStatus && !renderStatus));
		if (!eligable) {
			return null;
		}
		// If setting timeBetweenEmptyRetry=0, let's not fire off the rest immediately but after 2,3,4,.. seconds
		const multiplier = Math.max(this.timeBetweenEmptyRetry, this.emptyReturnRetries ? 1 : 0);
		return {
			delay: multiplier * (this.emptyReturnRetries + 1),
			remaining,
		};
	}

	divId() {
		return this.slot.getSlotElementId();
	}

	// State is not valid if the element is gone / have changed (on SPA page "switch")
	// Also check that there are more times to do normal reloads or that we're in an empty reload
	isStillValid() {
		return (this.divChangeOk || !(this.lastElementRef && this.lastElementRef !== this.element()))
			&& (this.leftToSchedule > 0 || this.currEmptyReload(true));
	}

	setHidden() {
		this.lastCountTs = null;
	}

	// To be called when we're about to be reloaded
	scheduleNextReload() {
		this.lastCountTs = null;
		this.countedTime = 0;
		delete this.emptyAdjustTs;
		delete this.areaTs;
		if (this.keepDims) { // Avoid "jumps" when going from an ad => no ad
			const div = this.element();
			if (div) {
				const style = getComputedStyle(div);
				div.style.minWidth = style.width;
				div.style.minHeight = style.height;
			}
		}
		const currEmpty = this.currEmptyReload();
		if (currEmpty) { // This is an empty reload
			this.emptyReturnRetries++;
			return currEmpty.remaining > 0;
		}
		this.leftToSchedule--;
		// Do we still have more reloads to do? We don't really know that if this is the last "normal" reload,
		// and it will be empty while 'retryOnEmptyReturn' is true.
		return this.leftToSchedule > 0 || this.retryOnEmptyReturn;
	}

	// Is the ad-div viewable according to 'Minium viewablity % for reload'
	isViewable() {
		const div = this.element();
		if (!div) {
			return false;
		}
		const rect = getClientRect(div);
		const { left, top } = rect;
		let { width, height } = rect;
		// For empty ad-slots, count viewability using the primary dimensions
		if (!width || !height) {
			if (!left && !top) {
				return false; // getClientRect probably failed to get the right rectangle, let's skip reload
			}
			[width, height] = this.adUnit.getPrimaryPrebidSize(); // use default size of placement
		}
		const { innerWidth, innerHeight } = window;
		const visibleWidth = Math.min(innerWidth, left + width) - Math.max(left, 0);
		const visibleHeight = Math.min(innerHeight, top + height) - Math.max(top, 0);
		const minPerc = this.minVisibility / 100;
		return visibleWidth > 0 && visibleHeight > 0 && (visibleWidth * visibleHeight) > (width * height * minPerc);
	}

	canResetEmptyRetries(updateTs) {
		if (!this.emptyReturnRetries) {
			return false; // no need to do the rest..
		}
		const renderStatus = this.renderStatus();
		if (!renderStatus || renderStatus.isEmpty) {
			return false;
		}
		if (!this.noSpaceIsEmpty) { // As soon as we're not empty, we can reset retries
			return true;
		}
		return this.areaTs && (updateTs - this.areaTs) >= EMPTY_CHECK_BUFFER_MS;
	}

	update(updateTs) {
		// Ignore this ReloadState if we haven't rendered the ad (e.g. by calling googletag.refresh())
		const renderStatus = this.renderStatus();
		if (!renderStatus) {
			this.lastCountTs = null;
			return;
		}
		const { isEmpty } = renderStatus;
		const element = this.element();

		const hasArea = element?.clientWidth && element?.clientHeight;
		if (!isEmpty && hasArea) {
			this.areaTs = this.areaTs || updateTs;
		} else {
			delete this.areaTs;
		}
		// Check (if enabled) if an "ad" was some empty space backfill
		if (this.noSpaceIsEmpty && element) {
			if (!isEmpty && !hasArea) { // It says we have an ad, but the area is empy
				this.emptyAdjustTs = this.emptyAdjustTs || updateTs;
				if ((updateTs - this.emptyAdjustTs) >= this.noSpaceTime * 1000) {
					this.setEmpty(true); // We've waited long enough during this condition, set ad as empty
				}
			} else if (this.emptyAdjustTs && hasArea) { // this.emptyAdjustTs => .isEmpty has been false
				// We were lucky... turned out this ad wasn't empty after all, reset it
				this.setEmpty(false);
				delete this.emptyAdjustTs;
			}
		}
		if (this.canResetEmptyRetries(updateTs)) {
			this.emptyReturnRetries = 0; // Reset after (really) getting an ad
		}
		// For checking if element dissapears/changes later (making this ReloadState not valid)
		this.lastElementRef = element;

		const viewable = this.isViewable();

		// Should we count down the time until next reload now? This depends on the "strategy"
		let countThis;

		if (this.strategy === 'load' || this.currEmptyReload()) { // always count the time after rendering
			countThis = true;
		} else if (this.strategy === 'viewStart') { // count time after being viewable once
			countThis = viewable || this.lastCountTs;
		} else { // this.strategy === 'sticky' => only count while being viewable
			countThis = viewable;
		}
		// If we counted down time until next reload during last update AND now, then add the time-span to 'countedTime'
		if (countThis && this.lastCountTs) {
			this.countedTime += updateTs - this.lastCountTs;
		}
		Utils.assign(this, {
			viewableNow: viewable,
			lastCountTs: countThis ? updateTs : null,
		});
	}

	isReloadReady() {
		const secNeeded = this.currEmptyReload()?.delay ?? this.timeBetween;
		return this.countedTime >= secNeeded * 1000 && this.viewableNow;
	}

	isReloadAllowed() {
		const {
			fType, filter, adUnit, lastInstance, reloader, blocked,
		} = this;
		const { hbWon } = lastInstance;

		if (blocked) {
			return false;
		}

		const response = adUnit.adserver.getSlotResponseInfo(this);
		if (!response || !this.filter.length) {
			return true;
		}

		const { seenLineItems } = reloader;
		const { adsType } = seenLineItems[adUnit.adserver.id]?.[response.lineItemId] || {};

		const checkMatch = ({ type, liType, value }) => {
			const idKey = type === 'order' ? 'orderId' : 'advertiserId';
			// eslint-disable-next-line eqeqeq
			const idMatches = !value || value == response[idKey];
			const liTypeMatches = !liType.length
				|| liType.includes(String(adsType))
				|| (liType.includes('hb') && hbWon)
				|| (!hbWon && !adsType && fType === 'excl');
			return idMatches && liTypeMatches;
		};
		const match = Utils.find(filter, checkMatch);
		return fType === 'incl' ? match : !match;
	}
}

module.exports = ReloadState;
